import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import QrAdPreview from './QrAdPreview';
import '../innovation-ads.css';

const LAYOUT_POSITIONS = {
    overlay: ['Bottom Left', 'Bottom Right'],
    sidePanel: ['Right', 'Left'],
};

const QrCodeConfigure = () => {
    const navigate = useNavigate();

    const [landingUrl, setLandingUrl] = useState('');
    const [ctaText, setCtaText] = useState('Know More');
    const [layout, setLayout] = useState('overlay'); // 'horizontalPanel' | 'overlay' | 'sidePanel'
    const [position, setPosition] = useState(LAYOUT_POSITIONS.overlay[0]);
    const [trackScans, setTrackScans] = useState(true);
    const [showMobilePreview, setShowMobilePreview] = useState(false);
    const tvVideoRef = useRef(null);

    // Pause the desktop/TV video while the mobile preview modal is open (so
    // both aren't playing at once), and resume it when the modal closes.
    useEffect(() => {
        const video = tvVideoRef.current;
        if (!video) return;
        if (showMobilePreview) {
            video.pause();
        } else {
            video.play().catch(() => {});
        }
    }, [showMobilePreview]);

    const positions = LAYOUT_POSITIONS[layout];

    const handleLayoutChange = (nextLayout) => {
        setLayout(nextLayout);
        setPosition(LAYOUT_POSITIONS[nextLayout][0]);
    };

    return (
        <div className="ia-page">
            <button type="button" className="ia-back-link" onClick={() => navigate('/innovation-ads')}>
                <i className="fa-solid fa-arrow-left"></i> Back to Innovation Ads
            </button>

            <h2 className="ia-title">QR Code</h2>
            <p className="ia-subheading">Configure your QR code enhancement settings.</p>

            <div className="ia-configure-grid">
                <div className="ia-panel">
                    <h3 className="ia-panel-title">Configure</h3>

                    <label className="ia-field-label">Landing Page URL</label>
                    <div className="ia-input-icon-wrap">
                        <input
                            type="text"
                            className="ia-field-input"
                            placeholder="https://"
                            value={landingUrl}
                            onChange={(e) => setLandingUrl(e.target.value)}
                        />
                        <i className="fa-solid fa-link"></i>
                    </div>

                    <label className="ia-field-label">Call to Action Text</label>
                    <input
                        type="text"
                        className="ia-field-input"
                        value={ctaText}
                        onChange={(e) => setCtaText(e.target.value)}
                    />

                    <label className="ia-field-label">LAYOUT</label>
                    <div className="ia-layout-toggle">
                        <button
                            type="button"
                            className={`ia-toggle-btn ${layout === 'overlay' ? 'active' : ''}`}
                            onClick={() => handleLayoutChange('overlay')}
                        >
                            Overlay
                        </button>
                        <button
                            type="button"
                            className={`ia-toggle-btn ${layout === 'sidePanel' ? 'active' : ''}`}
                            onClick={() => handleLayoutChange('sidePanel')}
                        >
                            Side Panel
                        </button>
                    </div>

                    <label className="ia-field-label">Position</label>
                    <select className="ia-select" value={position} onChange={(e) => setPosition(e.target.value)}>
                        {positions.map((p) => (
                            <option key={p} value={p}>{p}</option>
                        ))}
                    </select>

                    <div className="ia-divider" />

                    <div className="ia-toggle-row">
                        <button
                            type="button"
                            className={`ia-switch ${trackScans ? 'on' : ''}`}
                            onClick={() => setTrackScans(!trackScans)}
                            aria-pressed={trackScans}
                        >
                            <span className="ia-switch-knob" />
                        </button>
                        <span className="ia-toggle-text">Track Scans</span>
                    </div>
                    <p className="ia-toggle-help">Enable to track QR code scans and performance.</p>

                    <div className="ia-panel-footer">
                        <button type="button" className="ia-btn ia-btn-plain" onClick={() => navigate('/innovation-ads')}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="ia-btn ia-btn-primary"
                            onClick={() => setShowMobilePreview(true)}
                        >
                            Generate Mobile Preview
                        </button>
                    </div>
                </div>

                <div className="ia-panel">
                    <h3 className="ia-panel-title">Preview</h3>
                    <div className="ia-preview-wrap">
                        <div className="ia-preview-frame-tv">
                            <QrAdPreview layout={layout} position={position} ctaText={ctaText} videoRef={tvVideoRef} />
                        </div>
                        <div className="ia-preview-tv-stand" />
                        <p className="ia-simulated-note">
                            <i className="fa-solid fa-circle-info"></i>
                            This is a simulated preview. Actual appearance may vary by device and inventory.
                        </p>
                    </div>
                </div>
            </div>

            {showMobilePreview && (
                <div className="ia-modal-overlay" onClick={() => setShowMobilePreview(false)}>
                    <div className="ia-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ia-modal-header">
                            <h3 className="ia-modal-title">Mobile Preview</h3>
                            <button
                                type="button"
                                className="ia-modal-close"
                                onClick={() => setShowMobilePreview(false)}
                                aria-label="Close"
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>
                        <div className="ia-modal-body ia-modal-body-center">
                            <div className="ia-preview-frame-phone ia-preview-frame-phone-fullbleed">
                                <div className="ia-preview-phone-statusbar">
                                    <span className="icons">
                                        <i className="fa-solid fa-signal"></i>
                                        <i className="fa-solid fa-wifi"></i>
                                        <i className="fa-solid fa-battery-full"></i>
                                    </span>
                                </div>
                                <QrAdPreview layout={layout} position={position} ctaText={ctaText} isMobile />
                            </div>
                            <p className="ia-simulated-note">
                                <i className="fa-solid fa-circle-info"></i>
                                This is a simulated preview. Actual appearance may vary by device and inventory.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QrCodeConfigure;
